import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BUG_PRIORITIES,
  BUG_SEVERITIES,
  BUG_STATUSES,
  formatBugDate,
  getBugsForUser,
} from "../services/bugService";
import { useAuth } from "../hooks/useAuth";
import { routePaths } from "../constants/routes";
import "../style/bugTracker.css";

const defaultFilters = {
  status: "all",
  priority: "all",
  severity: "all",
  assignee: "all",
  module: "all",
  search: "",
};

function getStatusTone(status) {
  return String(status || "Open").toLowerCase().replaceAll(" ", "-");
}

export default function BugTracker() {
  const { user, role } = useAuth();
  const location = useLocation();
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(defaultFilters);

  useEffect(() => {
    let isMounted = true;

    async function loadBugs() {
      setLoading(true);

      try {
        const data = await getBugsForUser({ userId: user.uid, role });
        if (isMounted) setBugs(data);
      } catch (error) {
        console.error("Unable to load bugs:", error);
        toast.error("Unable to load bugs. Please check Firestore permissions.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadBugs();

    return () => {
      isMounted = false;
    };
  }, [role, user.uid]);

  const filterOptions = useMemo(() => {
    const modules = [...new Set(bugs.map((bug) => bug.module).filter(Boolean))].sort();
    const assignees = [...new Set(bugs.map((bug) => bug.assignedToName).filter(Boolean))].sort();
    return { modules, assignees };
  }, [bugs]);

  const stats = useMemo(() => {
    const open = bugs.filter((bug) => ["Open", "In Progress", "Reopened", "Retest"].includes(bug.status)).length;
    const critical = bugs.filter((bug) => bug.priority === "Critical" || bug.severity === "Blocker").length;
    const closed = bugs.filter((bug) => bug.status === "Closed").length;
    const reopened = bugs.filter((bug) => bug.status === "Reopened").length;
    return { total: bugs.length, open, critical, closed, reopened };
  }, [bugs]);

  const filteredBugs = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const pathScopedBugs = bugs.filter((bug) => {
      if (location.pathname === routePaths.myBugs) return bug.reportedById === user.uid;
      if (location.pathname === routePaths.assignedBugs) return bug.assignedToId === user.uid;
      return true;
    });

    return pathScopedBugs.filter((bug) => {
      if (filters.status !== "all" && bug.status !== filters.status) return false;
      if (filters.priority !== "all" && bug.priority !== filters.priority) return false;
      if (filters.severity !== "all" && bug.severity !== filters.severity) return false;
      if (filters.module !== "all" && bug.module !== filters.module) return false;
      if (filters.assignee !== "all" && bug.assignedToName !== filters.assignee) return false;

      if (!search) return true;
      return [bug.bugId, bug.title, bug.module, bug.assignedToName, bug.reportedByName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(search);
    });
  }, [bugs, filters, location.pathname, user.uid]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((currentFilters) => ({ ...currentFilters, [name]: value }));
  };

  return (
    <main className="bug-page">
      <section className="bug-hero">
        <div>
          <span className="bug-eyebrow">Issue Management</span>
          <h1>Bug Tracker</h1>
          <p>Create, assign, triage, and track QA defects with a Jira-style workflow inside QA Coder.</p>
        </div>
        <NavLink className="bug-primary-action" to={routePaths.bugsCreate}>
          + Raise Bug
        </NavLink>
      </section>

      <section className="bug-stats-grid">
        <article><span>Total Bugs</span><strong>{stats.total}</strong></article>
        <article><span>Open Bugs</span><strong>{stats.open}</strong></article>
        <article><span>Critical Bugs</span><strong>{stats.critical}</strong></article>
        <article><span>Closed Bugs</span><strong>{stats.closed}</strong></article>
        <article><span>Reopened Bugs</span><strong>{stats.reopened}</strong></article>
      </section>

      <section className="bug-panel">
        <div className="bug-panel-head">
          <div>
            <span className="bug-eyebrow">Bug Listing</span>
            <h2>Defect queue</h2>
          </div>
          <button type="button" className="bug-secondary-action" onClick={() => setFilters(defaultFilters)}>
            Reset Filters
          </button>
        </div>

        <div className="bug-filter-grid">
          <label>
            Search
            <input
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Bug ID, title, module..."
            />
          </label>
          <label>
            Status
            <select name="status" value={filters.status} onChange={handleFilterChange}>
              <option value="all">All Status</option>
              {BUG_STATUSES.map((status) => <option key={status}>{status}</option>)}
            </select>
          </label>
          <label>
            Priority
            <select name="priority" value={filters.priority} onChange={handleFilterChange}>
              <option value="all">All Priority</option>
              {BUG_PRIORITIES.map((priority) => <option key={priority}>{priority}</option>)}
            </select>
          </label>
          <label>
            Severity
            <select name="severity" value={filters.severity} onChange={handleFilterChange}>
              <option value="all">All Severity</option>
              {BUG_SEVERITIES.map((severity) => <option key={severity}>{severity}</option>)}
            </select>
          </label>
          <label>
            Assignee
            <select name="assignee" value={filters.assignee} onChange={handleFilterChange}>
              <option value="all">All Assignees</option>
              {filterOptions.assignees.map((assignee) => <option key={assignee}>{assignee}</option>)}
            </select>
          </label>
          <label>
            Module
            <select name="module" value={filters.module} onChange={handleFilterChange}>
              <option value="all">All Modules</option>
              {filterOptions.modules.map((module) => <option key={module}>{module}</option>)}
            </select>
          </label>
        </div>

        {loading ? (
          <div className="bug-empty-state">Loading bugs...</div>
        ) : filteredBugs.length ? (
          <div className="bug-table-wrap">
            <table className="bug-table">
              <thead>
                <tr>
                  <th>Bug ID</th>
                  <th>Title</th>
                  <th>Module</th>
                  <th>Priority</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Assignee</th>
                  <th>Reporter</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {filteredBugs.map((bug) => (
                  <tr key={bug.id}>
                    <td><NavLink to={routePaths.bugDetail(bug.id)}>{bug.bugId}</NavLink></td>
                    <td><strong>{bug.title}</strong><small>{bug.type}</small></td>
                    <td>{bug.module || "-"}</td>
                    <td><span className={`bug-pill priority-${bug.priority?.toLowerCase()}`}>{bug.priority}</span></td>
                    <td><span className={`bug-pill severity-${bug.severity?.toLowerCase()}`}>{bug.severity}</span></td>
                    <td><span className={`bug-pill status-${getStatusTone(bug.status)}`}>{bug.status}</span></td>
                    <td>{bug.assignedToName || "Unassigned"}</td>
                    <td>{bug.reportedByName || "-"}</td>
                    <td>{formatBugDate(bug.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bug-empty-state">
            <strong>No bugs found.</strong>
            <p>Raise a new bug or adjust filters to see matching defects.</p>
          </div>
        )}
      </section>
    </main>
  );
}
